<template>
  <div class="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50">
    <!-- Header -->
    <header class="bg-white">
      <div class="max-w-6xl mx-auto px-4 py-8 text-center">
        <h1 class="text-4xl font-bold text-gray-800 mb-2">📰 最新消息</h1>
<p class="text-lg text-gray-600">掌握平台最新功能與重要通知</p>
      </div>
    </header>

    <!-- Main Content -->
    <main class="max-w-6xl mx-auto px-4 py-8 -mb-3">
      <!-- News Grid -->
      <section class="mb-8">
        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
          <article
            v-for="news in currentNews"
            :key="news.id"
            class="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300"
          >
            <div class="aspect-video bg-gradient-to-r from-blue-100 to-purple-100 flex items-center justify-center">
              <img :src="news.image" :alt="news.title" class="w-full h-full object-cover" />
            </div>
            <div class="p-6">
              <div class="flex items-center gap-4 text-sm text-gray-500 mb-3">
                <div class="flex items-center gap-1">
                  <Calendar class="w-4 h-4" />
                  <span>{{ news.date }}</span>
                </div>
                <div class="flex items-center gap-1">
                  <User class="w-4 h-4" />
                  <span>{{ news.author }}</span>
                </div>
                <div class="flex items-center gap-1">
                  <Tag class="w-4 h-4" />
                  <span class="bg-[#E2F6F8] text-[#5FB4BF] px-2 py-1 rounded-full text-xs">
                    {{ news.category }}
                  </span>
                </div>
              </div>
              <h3 class="text-xl font-bold text-gray-800 mb-3 hover:text-[#5FB4BF] transition-colors">
                {{ news.title }}
              </h3>
              <p class="text-gray-600 leading-relaxed mb-4">{{ news.summary }}</p>
              <router-link 
                :to="`/news/${news.id}`" 
                class="bg-[#5FB4BF] hover:bg-[#4AA5B2] text-white px-4 py-2 rounded-lg transition-colors inline-block"
              >
                閱讀更多
              </router-link>
            </div>
          </article>
        </div>
      </section>

      <!-- Pagination -->
      <section class="flex justify-center items-center gap-2 mb-8">
        <button
          @click="handlePageChange(currentPage - 1)"
          :disabled="currentPage === 1"
          :class="[
            'flex items-center gap-2 px-4 py-2 rounded-lg transition-colors',
            currentPage === 1
              ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
              : 'bg-white text-[#5FB4BF] hover:bg-[#E2F6F8] shadow-md'
          ]"
        >
          <ChevronLeft class="w-4 h-4" />
          上一頁
        </button>

        <div class="flex gap-2">
          <button
            v-for="page in totalPages"
            :key="page"
            @click="handlePageChange(page)"
            :class="[
              'w-10 h-10 rounded-lg transition-colors',
              currentPage === page
                ? 'bg-[#5FB4BF] text-white shadow-lg'
                : 'bg-white text-[#5FB4BF] hover:bg-[#E2F6F8] shadow-md'
            ]"
          >
            {{ page }}
          </button>
        </div>

        <button
          @click="handlePageChange(currentPage + 1)"
          :disabled="currentPage === totalPages"
          :class="[
            'flex items-center gap-2 px-4 py-2 rounded-lg transition-colors',
            currentPage === totalPages
              ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
              : 'bg-white text-[#5FB4BF] hover:bg-[#E2F6F8] shadow-md'
          ]"
        >
          下一頁
          <ChevronRight class="w-4 h-4" />
        </button>
      </section>

      <!-- Page Info -->
      <div class="text-center text-gray-500 text-sm">
        第 {{ currentPage }} 頁，共 {{ totalPages }} 頁 | 總計 {{ newsList.length }} 則消息
      </div>
    </main>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'

const newsList = ref([
  {
    id: 1,
    title: '課程排程模組升級',
    summary: '我們的課程排程系統已全面升級，支援更彈性的排課設定與自動衝堂提醒功能，助您更有效率地管理課程安排。',
    date: '2025-07-01',
    author: '產品開發部',
    category: '功能更新',
    image: 'https://picsum.photos/seed/schedule/400/250'
  },
  {
    id: 2,
    title: '系統維護公告（7/15）',
    summary: '為提供更穩定的服務，系統將於 7 月 15 日凌晨 01:00 - 03:00 進行維護，期間系統將暫時無法使用，敬請見諒。',
    date: '2025-07-05',
    author: '技術支援部',
    category: '系統維護',
    image: 'https://picsum.photos/seed/maintenance/400/250'
  },
  {
    id: 3,
    title: '線上收費模組正式啟用',
    summary: '補教收費管理全面數位化！全新線上收費模組上線，支援多種支付方式與自動對帳，簡化您的收費流程。',
    date: '2025-06-25',
    author: '行銷部',
    category: '新功能上線',
    image: 'https://picsum.photos/seed/payment/400/250'
  },
  {
    id: 4,
    title: '家長App介面優化',
    summary: '根據使用者回饋，我們已對家長端 App 的操作介面進行優化，提升查詢成績、課表及訊息推播的易用性。',
    date: '2025-06-20',
    author: '設計部',
    category: 'UI/UX更新',
    image: 'https://picsum.photos/seed/parentapp/400/250'
  },
  {
    id: 5,
    title: '常見問題中心全新上線',
    summary: '我們全新推出線上 FAQ 支援中心，提供使用影片、操作手冊與即時搜尋，協助您快速解決常見問題。',
    date: '2025-06-10',
    author: '客服中心',
    category: '使用支援',
    image: 'https://picsum.photos/seed/faqcenter/400/250'
  },
  {
    id: 6,
    title: '暑期特別活動預告',
    summary: '我們將於 7 月 20 日舉辦暑期特別活動，包含線上講座、互動遊戲與抽獎活動，敬請期待！',
    date: '2025-06-05',
    author: '活動策劃部',
    category: '活動預告',
    image: 'https://picsum.photos/seed/summerevent/400/250'
  },
  {
    id: 7,
    title: '新功能：智能排課系統',
    summary: '我們的智能排課系統正式上線，能自動分析學生需求與教師時間，提供最佳排課建議。',
    date: '2025-05-28',
    author: '產品開發部',
    category: '新功能上線',
    image: 'https://picsum.photos/seed/autoschedule/400/250'
  },
  {
    id: 8,
    title: '系統升級通知',
    summary: '為提升系統穩定性，我們將於 6 月 15 日進行系統升級，期間可能會有短暫服務中斷，敬請見諒。',
    date: '2025-05-20',
    author: '技術支援部',
    category: '系統維護',
    image: 'https://picsum.photos/seed/systemupgrade/400/250'
  },
  {
    id: 9,
    title: '家長端App功能更新',
    summary: '家長端App已更新，新增成績查詢、課表查看與即時通知功能，讓家長隨時掌握孩子學習狀況。',
    date: '2025-05-15',
    author: '產品開發部',
    category: '功能更新',
    image: 'https://picsum.photos/seed/parentupdate/400/250'
  },
  {
    id: 10,
    title: '線上支付系統正式啟用',
    summary: '我們的線上支付系統已正式啟用，支援多種支付方式，讓收費更方便快捷。',
    date: '2025-05-10',
    author: '產品開發部',
    category: '新功能上線',
    image: 'https://picsum.photos/seed/onlinepayment/400/250'
  },
  {
    id: 11,
    title: '暑期特別活動預告',
    summary: '我們將於 7 月 20 日舉辦暑期特別活動，包含線上講座、互動遊戲與抽獎活動，敬請期待！',
    date: '2025-05-05',
    author: '活動策劃部',
    category: '活動預告',
    image: 'https://picsum.photos/seed/summerfun/400/250'
  },
  {
    id: 12,
    title: '新功能：智能排課系統',
    summary: '我們的智能排課系統正式上線，能自動分析學生需求與教師時間，提供最佳排課建議。',
    date: '2025-04-28',
    author: '產品開發部',
    category: '新功能上線',
    image: 'https://picsum.photos/seed/intelligent/400/250'
  }
])


const currentPage = ref(1)
const newsPerPage = 10

const totalPages = computed(() => Math.ceil(newsList.value.length / newsPerPage))

const currentNews = computed(() => {
  const start = (currentPage.value - 1) * newsPerPage
  return newsList.value.slice(start, start + newsPerPage)
})

const handlePageChange = (page) => {
  currentPage.value = page
  window.scrollTo({ top: 0, behavior: 'smooth' })
}
</script>